import React, { useState, useEffect } from 'react';
import { Loader, Alert } from './shared';

const FileStorageStats = ({ service, isOnline }) => {
  const [stats, setStats] = useState(null);
  const [storageStatus, setStorageStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [error, setError] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadStorageStatus = async () => {
    if (!isOnline) return;
    
    setStatusLoading(true);
    
    try {
      const response = await fetch(`${service.url}/storage/status`, {
        headers: {
          'Authorization': `Bearer ${service.apiKey}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStorageStatus(data);
      } else {
        console.warn('Failed to load storage status');
      }
    } catch (err) {
      console.warn('Error loading storage status:', err.message);
    } finally {
      setStatusLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadStats = async () => {
    if (!isOnline) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${service.url}/api/storage/stats`, {
        headers: {
          'Authorization': `Bearer ${service.apiKey}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        throw new Error('Failed to load stats');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
    loadStorageStatus();
    const interval = setInterval(() => {
      loadStats();
      loadStorageStatus();
    }, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [isOnline, loadStats, loadStorageStatus]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  if (!isOnline) {
    return (
      <Alert variant="error">
        Storage stats unavailable - service offline
      </Alert>
    );
  }

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error">
        Error loading stats: {error}
      </Alert>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6">
      {/* S3 Connection Status */}
      {storageStatus && (
        <div className={`border rounded-lg p-4 ${
          storageStatus.storage_mode === 'cloud'
            ? 'bg-green-50 border-green-200' 
            : 'bg-amber-50 border-amber-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`w-3 h-3 rounded-full mr-2 ${
                storageStatus.storage_mode === 'cloud' ? 'bg-green-500' : 'bg-amber-500'
              }`}></div>
              <h3 className="font-medium">
                {storageStatus.storage_mode === 'cloud' ? <><i className="fas fa-check-circle" style={{ marginRight: '8px', color: '#10b981' }}></i>S3 Cloud Storage Connected</> : <><i className="fas fa-exclamation-triangle" style={{ marginRight: '8px', color: '#f59e0b' }}></i>Using Local Storage</>}
              </h3>
            </div>
            {statusLoading && (
              <Loader size="xs" />
            )}
          </div>
          <div className="mt-2 space-y-1">
            <p className="text-sm">
              <strong>Storage Mode:</strong> {storageStatus.storage_mode}
            </p>
            {storageStatus.bucket_name && (
              <p className="text-sm">
                <strong>S3 Bucket:</strong> {storageStatus.bucket_name}
              </p>
            )}
            <p className="text-sm">
              <strong>Config Source:</strong> {storageStatus.credentials_source}
            </p>
            {storageStatus.guidance && (
              <div className="mt-3 p-3 bg-white bg-opacity-50 rounded border">
                <p className="text-sm font-medium mb-2">Setup Instructions:</p>
                {Array.isArray(storageStatus.guidance) ? (
                  <ol className="text-sm space-y-1 list-decimal list-inside">
                    {storageStatus.guidance.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ol>
                ) : (
                  <p className="text-sm">{storageStatus.guidance}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-gray-600">{stats.total_objects.toLocaleString()}</div>
          <div className="text-sm text-gray-700">Total Files</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600">{formatFileSize(stats.total_size)}</div>
          <div className="text-sm text-green-700">Total Storage</div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-purple-600">{stats.folders}</div>
          <div className="text-sm text-purple-700">Folders</div>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-orange-600">{Object.keys(stats.file_types).length}</div>
          <div className="text-sm text-orange-700">File Types</div>
        </div>
      </div>

      {/* File Types Breakdown */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-3">File Types</h3>
        <div className="space-y-2">
          {Object.entries(stats.file_types)
            .sort(([,a], [,b]) => b - a)
            .slice(0, 10)
            .map(([type, count]) => {
              const percentage = (count / stats.total_objects * 100).toFixed(1);
              return (
                <div key={type} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-[#FFC65D] rounded-full mr-2"></div>
                    <span className="text-sm font-mono">.{type}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">{count} files</span>
                    <span className="text-xs text-gray-500">({percentage}%)</span>
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Recent Files */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-3">Recent Files</h3>
        <div className="space-y-2">
          {stats.newest_files.slice(0, 5).map(file => (
            <div key={file.path} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
              <div className="flex items-center">
                <i className="fas fa-file" style={{ marginRight: '8px', color: '#000000' }}></i>
                <div>
                  <div className="text-sm font-medium">{file.name}</div>
                  <div className="text-xs text-gray-500">{file.path}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm">{formatFileSize(file.size)}</div>
                <div className="text-xs text-gray-500">
                  {new Date(file.modified).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FileStorageStats;